import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import NavBar from "../Comp/NavBar";
import SideBar from "../Comp/ProductList/ProductListSideBar";
import Item from "../Comp/ProductList/Item";
import Footer from "../Comp/Footer";
import { CircularProgress } from "@mui/material";

// Server-side paging. The page previously fetched every active row (~2.4k, ~1MB
// inflated) to render 48 of them, and filtered client-side. Now it pulls one
// PAGE_SIZE page at a time and pushes category/type/search filtering into SQL,
// so picking a category transfers only that category.
const PAGE_SIZE = 100;

// Fetch the next page while the shopper is still ~30 cards short of the end, so
// new rows land before they hit the bottom. Expressed as a viewport margin on
// the sentinel rather than a card count: it stays correct across screen sizes
// and column counts, where a fixed index does not.
const PREFETCH_MARGIN_PX = 1400;

// Cached at module scope so returning from a product does not refetch. Keyed by
// the active filter so switching category and coming back is still correct.
let PAGE_CACHE = { key: null, items: [], total: 0, scroll: 0 };

// Which element scrolls depends on CSS overflow rules, and assuming `window`
// silently breaks save/restore. Read and write through every candidate.
const getScrollY = () =>
  window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

const setScrollY = (y) => {
  window.scrollTo(0, y);
  document.documentElement.scrollTop = y;
  document.body.scrollTop = y;
};

const getScrollMax = () =>
  Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight;

const ProductList = () => {
  const location = useLocation();
  const q = (new URLSearchParams(location.search).get("q") || "").toLowerCase();
  // A category chip on the Home page navigates here with { state: { category_id } },
  // so landing on the shop from a chip pre-selects that category.
  const seededCat = location.state && location.state.category_id;
  const [filterForm, setFilterForm] = useState({
    type_id: [],
    category_id: seededCat ? [seededCat] : [],
    brand: [],
    company_id: [],
    price: "",
  });

  const filterKey = JSON.stringify({
    t: [...(filterForm.type_id || [])].sort(),
    c: [...(filterForm.category_id || [])].sort(),
    b: [...(filterForm.brand || [])].sort(),
    s: [...(filterForm.company_id || [])].sort(),
    q,
  });

  const resumable = PAGE_CACHE.key === filterKey && PAGE_CACHE.items.length > 0;

  const [items, setItems] = useState(() => (resumable ? PAGE_CACHE.items : []));
  const [total, setTotal] = useState(() => (resumable ? PAGE_CACHE.total : 0));
  const [isLoading, setIsLoading] = useState(!resumable);
  const [isPaging, setIsPaging] = useState(false);
  const [failed, setFailed] = useState(false);
  // On phones the filter panel is a drawer. Rendering it inline pushed all 100
  // products roughly two screens down behind a wall of category checkboxes.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilters = (filterForm.type_id || []).length + (filterForm.category_id || []).length
    + (filterForm.brand || []).length + (filterForm.company_id || []).length;

  const handleChangeMultiFilter = (id, value) => {
    setFilterForm((prev) => {
      const cur = prev[id] || [];
      return cur.includes(value)
        ? { ...prev, [id]: cur.filter((x) => x !== value) }
        : { ...prev, [id]: [...cur, value] };
    });
  };

  const clearFilters = () => setFilterForm({ type_id: [], category_id: [], brand: [], company_id: [], price: "" });

  // --- fetching ---------------------------------------------------------
  // A request counter guards against out-of-order responses: switch category
  // twice quickly and a slow first response must not overwrite the second.
  const reqSeq = useRef(0);
  const inFlight = useRef(false);

  const fetchPage = useCallback(
    (offset, key) => {
      if (inFlight.current) return;
      inFlight.current = true;
      const mySeq = ++reqSeq.current;
      const first = offset === 0;
      if (first) setIsLoading(true);
      else setIsPaging(true);

      const params = { offset, limit: PAGE_SIZE };
      const cats = filterForm.category_id || [];
      const types = filterForm.type_id || [];
      if (cats.length) params.category_id = cats.join(",");
      if (types.length) params.type_id = types.join(",");
      const brands = filterForm.brand || [];
      if (brands.length) params.brand = brands.join(",");
      const comps = filterForm.company_id || [];
      if (comps.length) params.company_id = comps.join(",");
      if (q) params.q = q;

      axios
        .get(`${process.env.REACT_APP_API_BASE_URL}/Public/getItemsPage`, { params })
        .then((res) => {
          if (mySeq !== reqSeq.current) return; // a newer filter won
          const body = res.data || {};
          const page = body.items || [];
          setItems((prev) => {
            const next = first ? page : [...prev, ...page];
            PAGE_CACHE = { key, items: next, total: body.total || 0, scroll: PAGE_CACHE.scroll };
            return next;
          });
          setTotal(body.total || 0);
          setFailed(false);
        })
        .catch(() => setFailed(true))
        .finally(() => {
          inFlight.current = false;
          setIsLoading(false);
          setIsPaging(false);
        });
    },
    [filterForm, q]
  );

  // A NEW search from the navbar resets leftover filters: searching "lg" with a
  // stale brand still ticked silently ANDs them into 0 results.
  const prevQ = useRef(q);
  useEffect(() => {
    if (prevQ.current !== q) {
      prevQ.current = q;
      if (q) setFilterForm({ type_id: [], category_id: [], brand: [], company_id: [], price: "" });
    }
  }, [q]);

  // First page on mount, and again whenever the filter or search changes.
  // Skipped when returning from a product with the same filter still cached.
  const bootKey = useRef(null);
  useEffect(() => {
    if (bootKey.current === filterKey) return;
    const returning = PAGE_CACHE.key === filterKey && PAGE_CACHE.items.length > 0;
    bootKey.current = filterKey;
    if (returning) {
      setItems(PAGE_CACHE.items);
      setTotal(PAGE_CACHE.total);
      setIsLoading(false);
      return;
    }
    setItems([]);
    setTotal(0);
    PAGE_CACHE = { key: filterKey, items: [], total: 0, scroll: 0 };
    sessionStorage.setItem("oot_shop_scroll", "0");
    setScrollY(0);
    fetchPage(0, filterKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const hasMore = items.length < total;

  // --- prefetch-ahead ---------------------------------------------------
  const sentinelRef = useRef(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || isLoading || failed) return;
    if (typeof IntersectionObserver === "undefined") return; // fallback button below
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) fetchPage(items.length, filterKey);
      },
      { rootMargin: `${PREFETCH_MARGIN_PX}px 0px` }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [items.length, hasMore, isLoading, failed, fetchPage, filterKey]);

  // --- scroll position across back-navigation ---------------------------
  // Capture phase: if a CSS rule makes <body> the scroller, the scroll event
  // fires on body and never bubbles to window.
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const onScroll = () => {
      const y = getScrollY();
      if (y > 0) {
        sessionStorage.setItem("oot_shop_scroll", String(y));
        PAGE_CACHE.scroll = y;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, { capture: true });
  }, []);

  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || isLoading || !items.length) return;
    const y = parseInt(sessionStorage.getItem("oot_shop_scroll") || "0", 10);
    restored.current = true;
    if (y <= 0) return;
    // Cards and their lazy images have not laid out yet, so the document is
    // short and the scroll clamps. Re-apply while the page grows.
    let tries = 0;
    const tick = () => {
      const max = getScrollMax();
      setScrollY(Math.min(y, Math.max(max, 0)));
      if (max >= y || tries >= 100) return; // ~5s
      tries += 1;
      setTimeout(tick, 50);
    };
    tick();
  }, [isLoading, items]);

  const shownCount = items.length;

  return (
    <>
      <NavBar />
      <div className="oot-page">
        <div className="oot-shop-head">
          <h1>{q ? `Results for “${q}”` : "Shop"}</h1>
          <span className="count">
            {isLoading ? "loading…" : `${shownCount} of ${total} products`}
          </span>
          <button
            className="oot-filters-btn oot-btn oot-btn-ghost"
            onClick={() => setFiltersOpen(true)}
          >
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round">
              <path d="M3 6h18M7 12h10M11 18h2" />
            </svg>
            Filters{activeFilters > 0 ? ` (${activeFilters})` : ""}
          </button>
        </div>

        <div className="oot-shop-layout">
          {filtersOpen && (
            <div className="oot-filters-backdrop" onClick={() => setFiltersOpen(false)} />
          )}
          <aside className={`oot-sidebar${filtersOpen ? " open" : ""}`}>
            <button className="oot-filters-close" onClick={() => setFiltersOpen(false)} aria-label="Close filters">×</button>
            <SideBar
              filterForm={filterForm}
              handleChangeMultiFilter={handleChangeMultiFilter}
              clearFilters={clearFilters}
            />
            <button className="oot-filters-done oot-btn oot-btn-primary oot-btn-lg"
              onClick={() => setFiltersOpen(false)}>
              {isLoading ? "Show products" : `Show ${total} products`}
            </button>
          </aside>

          <main>
            <div className="oot-grid">
              {isLoading ? (
                <div className="oot-spinner-wrap"><CircularProgress /></div>
              ) : items.length ? (
                items.map((item) => <Item key={item.id} data={item} />)
              ) : failed ? (
                <div className="oot-empty">Could not load products. Please try again.</div>
              ) : (
                <div className="oot-empty">No products match your filters.</div>
              )}
            </div>

            {/* Prefetch trigger: sits below the grid and fires early via rootMargin. */}
            <div ref={sentinelRef} style={{ height: 1 }} />

            {!isLoading && isPaging && (
              <div style={{ textAlign: "center", marginTop: 22 }}><CircularProgress size={26} /></div>
            )}

            {/* Fallback for no IntersectionObserver, and a retry path after an error. */}
            {!isLoading && !isPaging && hasMore && (
              <div style={{ textAlign: "center", marginTop: 26 }}>
                <button
                  className="oot-btn oot-btn-ghost oot-btn-lg"
                  onClick={() => fetchPage(items.length, filterKey)}
                >
                  {failed ? "Retry" : `Load more (${total - items.length} left)`}
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ProductList;
